import { usePostProductSurvey } from '@/hooks/rest/usePostProductSurvey';
import { SectionBody } from '@/shared-ui/PageElements/SectionBody';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { CircularProgress, Stack, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import React, { useEffect, useMemo, useState } from 'react';

import type { FeedbackPayload } from '@/components/PatientPageV2/components/PrescriptionPad';
import {
  PrescriptionPadStep,
  PrescriptionPadWrapper,
  transformAndMergeMedicationsAndItemsToReissue,
  transformFeedbackPayload,
  transformV3DeviceProductsToV4DeviceProducts,
} from '@/components/PatientPageV2/components/PrescriptionPad';
import { useAccessControl } from '@/hooks';
import { useGetPrescribedProducts } from '@/hooks/prescribedProducts';
import { usePrescribePrescriptions } from '@/hooks/prescription/usePrescribePrescriptions';
import { useAppStore } from '@/state-management';
import type { PrescriptionAttributes, TypedResponse } from '@/types';
import { UserRole } from '@/types';

import { QueryKey } from '@/data/query';
import { getUser } from '@/data/service/authService';
import FetchFailedError from '@/error-handling/components/FetchFailedError';
import type { NetworkError } from '@/error-handling/types';
import { StatusCode } from '@/error-handling/types';
import { usePatientPrescriptions } from '@/hooks/patients/usePatientPrescriptions';
import { useSubmitCancelPrescribedMedications } from '@/hooks/prescribedProducts/useSubmitCancelPrescribedMedications';
import { SectionHeader } from '@/shared-ui/PageElements/SectionHeader';
import { ConfirmDialog, toast } from '@montugroup/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useModal } from 'mui-modal-provider';
import { listSorter } from '../../assets/js/util';
import { makeDelete, makeGET, makePUT } from '../../data/service/dataService';
import { StoreContext } from '../../data/store';
import MaterialTablePrescription from '../../pages/MaterialTablePrescription';
import { getCancelledOrDeletedStatus } from '../../utils/prescription';
import { useDispenseLimitExceededActions } from '../Patient/hooks/useDispenseLimitExceededActions';
import CursorPagination from '../tables/components/CursorPagination';
import { CancelPrescribedMedicationModal } from './components/CancelPrescribedMedicationModal/CancelPrescribedMedicationModal';
import type { PatientDetails } from './components/ConfirmPrescriptionCard/ConfirmPrescriptionCard.types';
import { PrescribeErrorModal } from './components/PrescribeErrorModal/PrescribeErrorModal';

type PrescriptionsProps = {
  patientId: string;
  patientActive: boolean;
  classes?: any;
  doctor: { id: number; prescriber_number: string; DoctorUser: any };
  patient: PatientDetails;
  isLoading: boolean;
};
interface PrescriptionRow {
  order_code: string;
  script: number;
  Status: string;
  Date: string;
  delete_id: number;
  refillRows: PrescribedProductRow[] | [];
}

interface PrescribedProductRow {
  id: number;
  prescriptionId: string;
  prescriptionIdSearch: string;
  date: string;
  medication: string;
  medicationSearch: string;
  quantity: number | string;
  script: string | null;
  script_id: number;
  cancel_id?: number;
  delete_product_id?: string;
  group_id: string;
  Status: string;
  parent: number;
  child_prescriptions?: string[];
  repeat: number;
  parentPrescriptionId?: string;
  child_prescription?: string;
}
const Prescriptions = (props: PrescriptionsProps) => {
  const { showModal } = useModal();
  const {
    handlePrescriptionsPaginationChange,
    patientPrescriptionsData,
    responsePrescriptionsCursors,
    setPrescriptionsCursors,
    refetchPrescriptions,
    isPatientPrescriptionsLoading,
    setPrescriptionsSearch,
    isPatientPrescriptionsError,
  } = usePatientPrescriptions(Number(props.patientId));

  const prescriptionData = patientPrescriptionsData ? listSorter(patientPrescriptionsData, 'order_date') : [];

  const { refetch: refetchPrescribedProducts } = useGetPrescribedProducts({
    patientId: `${props?.patientId ?? ''}`,
  });

  const [addScript, setAddScript] = React.useState(false);
  const [value, setValue] = React.useState('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const { submitCancelPrescribedMedications, isLoading: isCancellingPrescriptions } =
    useSubmitCancelPrescribedMedications();
  const [prescribeStateError, setPrescribeStateError] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    state: {
      authState: { user },
    },
  } = React.useContext(StoreContext);
  const { allowAccess } = useAccessControl();

  const {
    prescriptionPad: {
      step: createScriptStep,
      devicePadItems: devicePadItems,
      medicationPadItems: medicationPadItems,
      actions: reviewActions,
    },
    prescriptionHistory: { itemsToAction, itemsToCancel, itemsToReissue },
  } = useAppStore.use.prescriptionAssistant();
  const updatePrescriptionPadStep = useAppStore.use.updatePrescriptionPadStep();
  const resetPrescriptionPad = useAppStore.use.resetPrescriptionPad();
  const resetPrescriptionHistory = useAppStore.use.resetPrescriptionHistory();
  const isValidPrescriptionPad = useAppStore.use.isValidPrescriptionPad()();
  const isPrescriptionPadInSync = useAppStore.use.isPrescriptionPadInSync()();
  const productSurveyFeedbackPayload = transformFeedbackPayload(medicationPadItems);
  const { shouldDisable: shouldDisableDueToDispenseLimitExceeded } = useDispenseLimitExceededActions();

  const medicationsPrescribeEndpointPayload = transformAndMergeMedicationsAndItemsToReissue(
    medicationPadItems,
    itemsToReissue,
  );
  const hasCompletedActions = itemsToAction.length === 0;
  const productSurveyMutation = usePostProductSurvey();
  const queryClient = useQueryClient();

  const { mutateAsync: prescribePrescriptions } = usePrescribePrescriptions({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.PrescribedProducts] });
      submitProductSurveyFeedback(productSurveyFeedbackPayload, props.doctor.id, props.patientId);
    },
    onError: (error) => {
      const networkError = error as NetworkError;
      if (networkError.status === StatusCode.Conflict) {
        setPrescribeStateError(true);
        refetchPrescriptions();
      } else {
        showErrorNotification();
      }
    },
    onSettled: () => {
      finaliseProcess();
    },
    meta: { isErrorHandled: true },
  });

  const [buttonHovered, setButtonHovered] = useState(false);

  const handleDeleteRecord = async (prescriptionId: string) => {
    const resp = await makeDelete(`prescription?id=${prescriptionId}`, {}, 'delete-prescription');
    if (resp) {
      toast.success('Delete the Prescription Successfully');
      refetchPrescriptions();
    } else {
      toast.error('Cannot delete the Prescription');
    }
  };

  const deleteRecord = (prescriptionId: string) => {
    if (!props.patientActive) {
      return;
    }

    const modal = showModal(
      ConfirmDialog,
      {
        title: 'Delete prescription',
        children: 'Are you sure want to delete this prescription?',
        actions: (
          <>
            <Button
              size="large"
              onClick={() => {
                modal.hide();
              }}
            >
              Cancel
            </Button>
            <Button
              size="large"
              color="error"
              onClick={() => {
                modal.hide();
                handleDeleteRecord(prescriptionId);
              }}
            >
              Delete
            </Button>
          </>
        ),
      },
      // prevent modal from closing when backdrop is clicked
      { hideOnClose: false },
    );
  };
  // Show Delete only for Super-admins and Doctors
  const validateRole = () => {
    if ([1, 4, 6].includes(JSON.parse(user as string).role_id)) {
      return true;
    }
    return false;
  };

  const getUrlOfPrescription = (id: any, zeroRepeat: boolean, parent: number) => {
    const apiPath =
      zeroRepeat && parent === 2
        ? `prescription/getPathOfRefillPrescription/${id}`
        : `prescription/getPathOfPrescription/${id}`;
    makeGET(apiPath, 'prescriptions').then((res: { url: string } | null) => {
      if (res && res.url) {
        window.open(res.url);
      } else {
        toast.error('Prescription not found!');
      }
    });
  };

  const handleCancelPrescription = async (cancelId: number) => {
    // call the delete api
    const resp = await makePUT(`prescription/${cancelId}`, {}, 'cancel-prescription');
    if (resp && resp.data.success) {
      toast.success(resp.data.message);
      refetchPrescriptions();
      refetchPrescribedProducts();
    } else {
      toast.error(resp?.data.message || 'Cancel prescription failed');
    }
  };

  const cancelRecord = (cancelId: number, prescriptionID: string) => {
    const modal = showModal(
      ConfirmDialog,
      {
        title: 'Cancel eScript',
        children: <>Are you sure you want to cancel eScript - {prescriptionID}?</>,
        actions: (
          <>
            <Button
              color="error"
              size="large"
              onClick={() => {
                modal.hide();
                handleCancelPrescription(cancelId);
              }}
            >
              Yes
            </Button>
            <Button
              color="secondary"
              size="large"
              onClick={() => {
                modal.hide();
              }}
            >
              Cancel
            </Button>
          </>
        ),
      },
      // prevent modal from closing when backdrop is clicked
      { hideOnClose: false },
    );
  };

  const [debouncedValue, setDebouncedValue] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, 1000);

    return () => clearTimeout(handler);
  }, [value]);

  useEffect(() => {
    if (setPrescriptionsSearch && setPrescriptionsCursors) {
      setPrescriptionsCursors({ prev: null, next: null });
      setPrescriptionsSearch(debouncedValue);
    }
  }, [debouncedValue, setPrescriptionsSearch, setPrescriptionsCursors]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    setValue(event.target.value);
  };

  const DataFormatter = (
    data: { Product: { name: string }; other_product: any; quantity: any; repeats: number },
    type: string,
  ) => {
    if (type === 'productName') {
      return (data.Product && data.Product.name) || data.other_product;
    } else if (type === 'quantity') {
      return <ul style={{ listStyle: 'none' }}>{data.quantity}</ul>;
    } else if (type === 'repeats') {
      return <ul style={{ listStyle: 'none' }}>{data.repeats}</ul>;
    }
  };

  const rowHeader: PrescriptionRow[] = [];
  let id = 1;
  for (const element of prescriptionData) {
    let count = 0;
    const prescribedProductsRows = [];

    for (const prescribedProducts of element.PrescribedProducts) {
      const refillPrescriptionRow = [];
      const prescribedProductsData = {
        id: ++id,
        prescriptionId: prescribedProducts.prescription_product_code || element.order_code,
        prescriptionIdSearch: prescribedProducts.prescription_product_code || element.order_code,
        date: element.order_date,
        medication: DataFormatter(prescribedProducts, 'productName'),
        medicationSearch: DataFormatter(prescribedProducts, 'productName'),
        quantity: DataFormatter(prescribedProducts, 'quantity'),
        script: element.url,
        script_id: element.id,
        cancel_id: prescribedProducts.id,
        delete_product_id: (prescribedProducts.Product && prescribedProducts.Product.formulation_id) || '',
        group_id: prescribedProducts.prescription_product_code || element.order_code,
        Status: prescribedProducts.active
          ? element.PrescriptionStatus.name
          : getCancelledOrDeletedStatus(element.order_code),
        parent: 1,
        child_prescriptions: [] as any[],
        repeat: 0,
        parentPrescriptionId: undefined,
        child_prescription: undefined,
      };
      const childPrescription = [];
      const refillsPrescriptionsIdsArr: any[] = [];
      for (let k = 0; k < prescribedProducts.RefillPrescribedProducts.length; k++) {
        const refillPrescription = prescribedProducts.RefillPrescribedProducts[k];
        if (!refillsPrescriptionsIdsArr.includes(refillPrescription.refill_prescription_id)) {
          refillsPrescriptionsIdsArr.push(refillPrescription.refill_prescription_id);
        }
        const refillPrescriptionData = {
          id: ++id,
          prescriptionId: refillPrescription.refill_prescription_product_code || element.order_code,
          prescriptionIdSearch: refillPrescription.refill_prescription_product_code || element.order_code,
          parentPrescriptionId: element.order_code,
          date: refillPrescription.created_date,
          medication: refillPrescription.refill_prescription_product_code || element.order_code,
          medicationSearch: DataFormatter(prescribedProducts, 'productName'),
          quantity: refillPrescription.quantity,
          repeat: 0,
          script: refillPrescription.RefillPrescription
            ? refillPrescription.RefillPrescription.refill_prescription_url
            : null,
          script_id: refillPrescription.id,
          group_id: prescribedProducts.prescription_product_code || element.order_code,
          Status: !refillPrescription.active ? 'Cancelled' : element.PrescriptionStatus.name,
          parent: 2,
          base_id: prescribedProducts.prescription_product_code || element.order_code,
          isFinal: k === prescribedProducts.RefillPrescribedProducts.length - 1 ? true : false,
          child_prescription: undefined,
        };
        refillPrescriptionRow.push(refillPrescriptionData);
        childPrescription.push(refillPrescription.refill_prescription_product_code || element.order_code);
        id++;
      }
      count++;
      prescribedProductsData.child_prescriptions = childPrescription;
      prescribedProductsData.repeat = DataFormatter(prescribedProducts, 'repeats');
      prescribedProductsRows.push(prescribedProductsData);
      prescribedProductsRows.push(...refillPrescriptionRow);
      id++;
    }

    const headerData: PrescriptionRow = {
      order_code: element.order_code + ' (' + count + ')',
      script: element.id,
      Status: element.PrescriptionStatus.name,
      Date: DateTime.fromISO(element.order_date).toFormat('dd-MMM-yyyy'),
      delete_id: element.id,
      refillRows: prescribedProductsRows,
    };
    rowHeader.push(headerData);
  }

  let refillColumn = [
    {
      title: 'id',
      field: 'id',
      hidden: true,
      sorting: false,
      grouping: false,
      filtering: false,
      cellStyle: { width: '1%' },
    },
    {
      title: '',
      field: 'prescriptionId',
      sorting: true,
      grouping: false,
      filtering: true,
      hidden: true,
      cellStyle: { width: '20%' },
    },
    {
      title: 'Product',
      sort: false,
      field: 'medication',
      filtering: true,
      display: true,
      cellStyle: (cellValue: string) => {
        const fontWeight = cellValue?.startsWith('ALEP') || cellValue?.startsWith('ALPR') ? '' : 'Bold';
        return { fontWeight, minWidth: 220, maxWidth: 220 };
      },
    },
    {
      title: 'Quantity',
      field: 'quantity',
      grouping: false,
      sorting: false,
      filtering: false,
      align: 'center',
      cellStyle: { minWidth: 50, maxWidth: 50 },
    },
    {
      title: 'Repeats',
      field: 'repeat',
      grouping: false,
      sorting: false,
      filtering: false,
      align: 'center',
      cellStyle: { paddingLeft: 'none', minWidth: 30, maxWidth: 30 },
    },
    {
      title: 'Status',
      field: 'Status',
      grouping: false,
      sorting: false,
      filtering: false,
      cellStyle: { width: '8%', minWidth: 50, maxWidth: 50 },
    },
    {
      title: 'Date',
      field: 'date',
      grouping: false,
      sorting: true,
      filtering: false,
      align: 'center',
      cellStyle: { paddingLeft: 'none', minWidth: 100, maxWidth: 100, textAlign: 'center' },
      render: (rowdata: { date: string }) => {
        return DateTime.fromISO(rowdata.date).toFormat('dd-MMM-yyyy');
      },
    },
    {
      title: '',
      field: 'script',
      grouping: false,
      sorting: false,
      filtering: false,
      cellStyle: { minWidth: 20, maxWidth: 20, cursor: 'pointer' },
      align: 'center',
      render: (rowData: {
        script: any;
        script_id: any;
        Status: string;
        parent: number;
        prescriptionId: string;
        cancel_id: number;
      }): React.ReactNode =>
        rowData.script ? (
          <Link onClick={() => getUrlOfPrescription(rowData.script_id, true, rowData.parent)}>
            <VisibilityIcon fontSize="small" style={{ color: 'black' }} />
          </Link>
        ) : (
          'Creating...'
        ),
    },
  ];

  const deleteBtn = {
    title: '',
    field: 'cancel_id',
    grouping: false,
    sorting: false,
    filtering: false,
    cellStyle: { minWidth: 20, maxWidth: 20, cursor: 'pointer' },
    align: 'center',
    render: (rowData: {
      script: any;
      script_id: any;
      Status: string;
      parent: number;
      prescriptionId: string;
      cancel_id: number;
    }): React.ReactNode => {
      if (rowData.Status !== 'Active' || !props.patientActive) {
        return null;
      }
      if (rowData.parent === 2 || (rowData.prescriptionId && rowData.prescriptionId.substr(0, 4) === 'ALPR')) {
        return <DeleteIcon fontSize="small" style={{ color: 'transparent' }} />;
      }
      return (
        <Link
          onClick={() => {
            cancelRecord(rowData.cancel_id, rowData.prescriptionId);
          }}
          aria-label={`delete prescription for product ${rowData.prescriptionId}`}
        >
          <DeleteIcon fontSize="small" style={{ color: 'black' }} />
        </Link>
      );
    },
  };

  const shouldAllowDeleteBtn = ![UserRole.Admin].includes(getUser()?.role_id);

  if (shouldAllowDeleteBtn) {
    refillColumn = [...refillColumn, deleteBtn];
  }

  const paperRefillColumn = refillColumn.map((a) => ({ ...a }));

  paperRefillColumn[1].hidden = true;

  const handleCreateNewScript = () => {
    updatePrescriptionPadStep(PrescriptionPadStep.Create);
    setAddScript(true);
  };

  const handleCreateNewScriptBtn = () => {
    const createNewScriptBtn = (
      <Button
        variant="contained"
        color="secondary"
        size="large"
        className={props.classes?.button}
        disabled={!props.patientActive}
        onClick={handleCreateNewScript}
        style={{ marginLeft: '0.5rem' }}
        aria-label="create new script"
      >
        Create New Script
      </Button>
    );
    return allowAccess([UserRole.Doctor], createNewScriptBtn);
  };

  const waitingToCancelItems = itemsToCancel.length > 0;
  const noItemsToAction = itemsToAction.length === 0;

  const handleResponse = async (response: TypedResponse<PrescriptionAttributes>) => {
    if (!response || response.status >= 300) {
      throw new Error(`Unexpected response status: ${response?.status}`);
    }

    // @ts-expect-error: TS2339: type needs to be fixed
    const prescriptionCode = response.body.prescription.orderCode;

    toast.success(`Prescription - ${prescriptionCode} has been processed and ePrescription is also created!`);
    refetchPrescriptions();
  };

  const submitProductSurveyFeedback = async (feedbackPayload: FeedbackPayload[], doctorId: any, patientId: any) => {
    if (feedbackPayload?.length) {
      productSurveyMutation?.mutate({
        doctorId,
        patientId,
        feedback: feedbackPayload,
      });
    }
  };

  const showErrorNotification = () => {
    toast.error({
      title: 'Prescription Not Created',
      message: 'Error happened while eprescription creation, Please try again!',
    });
  };

  const finaliseProcess = () => {
    setLoading(false);
    resetPrescriptionPad();
    resetPrescriptionHistory();
    updatePrescriptionPadStep(PrescriptionPadStep.Create);
    refetchPrescribedProducts();
    setAddScript(false);
  };

  const canOpenCancelModal = () => noItemsToAction && waitingToCancelItems && !isCancelModalOpen;

  const onReviewScript = () => {
    canOpenCancelModal() ? setIsCancelModalOpen(true) : handleCreateScript();
  };

  const handleSubmitScript = async () => {
    setLoading(true);
    const response = await createPrescriptionAPI();
    // @ts-expect-error: TS2345: type needs to be fixed
    await handleResponse(response);
  };

  const handleCreateScript = () => {
    if (createScriptStep === PrescriptionPadStep.Create) {
      updatePrescriptionPadStep(PrescriptionPadStep.Confirm);
      return;
    }

    handleSubmitScript();
  };

  const createPrescriptionAPI = async () => {
    const validDevices = devicePadItems.filter((device) => !!device);
    const payload = {
      orderDate: new Date(),
      products: [...medicationsPrescribeEndpointPayload],
      devices: transformV3DeviceProductsToV4DeviceProducts(validDevices),
    };

    return prescribePrescriptions({
      params: { patientId: props.patientId as string },
      // @ts-expect-error: TS2339: type needs to be fixed
      //orderDate is expected to be a string
      body: payload,
    });
  };

  const onScriptSubmit = () => {
    onReviewScript();
  };

  const handleCancelScript = () => {
    resetPrescriptionPad();
    resetPrescriptionHistory();
    updatePrescriptionPadStep(PrescriptionPadStep.Create);
    setAddScript(false);
  };

  const handleConfirmCancelPrescription = () => {
    setIsCancelModalOpen(false);

    const hasNoValidMedication = !medicationPadItems[0]?.medicationPadItem?.productName;

    if (hasNoValidMedication) {
      // Doctor is only cancelling medications
      resetPrescriptionPad();
      resetPrescriptionHistory();
      updatePrescriptionPadStep(PrescriptionPadStep.Create);
      refetchPrescribedProducts();
      refetchPrescriptions();
      setAddScript(false);
    } else {
      // Doctor is both cancelling and prescribing medications
      updatePrescriptionPadStep(PrescriptionPadStep.Confirm);
    }
  };

  const disableCreateScriptButton = loading || !isValidPrescriptionPad || !isPrescriptionPadInSync;

  const disabledDueToUnactionedConfirmCheckList = useMemo(
    () => createScriptStep === PrescriptionPadStep.Confirm && reviewActions.some((action) => !action.checked),
    [createScriptStep, reviewActions],
  );

  const tooltipMsg = useMemo(() => {
    if (!isPrescriptionPadInSync) {
      return 'Prescription data is outdated. Reload to continue.';
    }
    if (shouldDisableDueToDispenseLimitExceeded) {
      return 'Please add a dispense note to proceed';
    }
    if (disabledDueToUnactionedConfirmCheckList) {
      return 'Please complete the required actions checklist';
    }
    if (!hasCompletedActions) {
      return 'Please select reissue or cancel for current active scripts to proceed';
    }

    return '';
  }, [
    isPrescriptionPadInSync,
    disabledDueToUnactionedConfirmCheckList,
    shouldDisableDueToDispenseLimitExceeded,
    hasCompletedActions,
  ]);

  const hidePrescribeStateError = () => {
    setPrescribeStateError(false);
  };

  const shouldDisableScriptBtn = useMemo(() => {
    return (
      disableCreateScriptButton ||
      disabledDueToUnactionedConfirmCheckList ||
      shouldDisableDueToDispenseLimitExceeded ||
      !hasCompletedActions
    );
  }, [
    disableCreateScriptButton,
    disabledDueToUnactionedConfirmCheckList,
    shouldDisableDueToDispenseLimitExceeded,
    hasCompletedActions,
  ]);

  const renderCancelAndCreateScriptButtons = () => {
    if (!hasCompletedActions) {
      return null;
    }

    const enabledTooltip = disabledDueToUnactionedConfirmCheckList || !isPrescriptionPadInSync;

    return (
      <Stack flexDirection="row-reverse" mt={14}>
        <Tooltip
          open={enabledTooltip && buttonHovered}
          title={tooltipMsg}
          placement="top-start"
          disableFocusListener
          disableTouchListener
          arrow
        >
          <Box
            onMouseEnter={() => {
              if (enabledTooltip) {
                setButtonHovered(true);
              }
            }}
            onMouseLeave={() => setButtonHovered(false)}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={onScriptSubmit}
              size="large"
              disabled={shouldDisableScriptBtn}
              startIcon={loading ? <CircularProgress size={16} /> : null}
              sx={{
                marginLeft: '4px',
                borderRadius: 1,
                textTransform: 'none',
                letterSpacing: 1,
                fontWeight: '600',
                boxShadow:
                  '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)',
              }}
            >
              {`${createScriptStep === PrescriptionPadStep.Create ? 'Review' : 'Create'} Script`}
            </Button>
          </Box>
        </Tooltip>

        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={handleCancelScript}
          disabled={loading}
          sx={{ borderRadius: 1, textTransform: 'none', letterSpacing: 1, fontWeight: 'bold' }}
        >
          Cancel
        </Button>
      </Stack>
    );
  };

  const renderCreateScript = () => (
    <>
      <PrescriptionPadWrapper
        doctorId={props.doctor.id}
        patientId={props.patientId}
        prescriberNumber={props.doctor?.prescriber_number}
        doctorPatientDetails={{
          doctor: props.doctor.DoctorUser,
          patient: props.patient,
        }}
      />
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={4} my={10}>
        <CancelPrescribedMedicationModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={() =>
            submitCancelPrescribedMedications(props.patientId!.toString(), handleConfirmCancelPrescription)
          }
          isLoading={isCancellingPrescriptions}
        />

        {renderCancelAndCreateScriptButtons()}
      </Stack>
    </>
  );

  const PrescriptionsResults = () => {
    if (isPatientPrescriptionsError) {
      return <FetchFailedError />;
    }
    return (
      <div>
        {isPatientPrescriptionsLoading ? (
          <Box justifyContent="center" alignItems="center" gap="1" display="flex" height="100vh">
            <CircularProgress size={50} />
          </Box>
        ) : (
          <>
            {prescriptionData?.length > 0 ? (
              rowHeader.map((headerData, index) => (
                <div
                  key={index}
                  className={headerData?.order_code.startsWith('ALE') ? 'border-group-e-script' : 'border-group-paper'}
                >
                  <MaterialTablePrescription
                    refillColumn={headerData?.order_code.startsWith('ALE') ? refillColumn : paperRefillColumn}
                    refillRows={headerData?.refillRows}
                    rowHeader={headerData}
                    getUrlOfPrescription={getUrlOfPrescription}
                    deleteRecord={deleteRecord}
                    validateRole={validateRole}
                    patientActive={props.patientActive}
                  ></MaterialTablePrescription>
                </div>
              ))
            ) : (
              <div style={{ textAlign: 'center', marginTop: '5%' }}>No matching records found</div>
            )}

            {prescriptionData?.length > 0 && (
              <CursorPagination
                handlePagination={handlePrescriptionsPaginationChange}
                hasPrevPage={Boolean(responsePrescriptionsCursors?.prev)}
                hasNextPage={Boolean(responsePrescriptionsCursors?.next)}
              />
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <SectionHeader headerText="Prescriptions" />
      <SectionBody paddingX={0} paddingTop={4}>
        {addScript ? (
          renderCreateScript()
        ) : (
          <div style={{ width: '100%' }}>
            <Box display="flex" justifyContent="space-between">
              <Box mt={1} height="0.5">
                <TextField
                  id="outlined-multiline-flexible"
                  variant="outlined"
                  placeholder="Search"
                  maxRows={4}
                  value={value}
                  onChange={(e) => handleChange(e as React.ChangeEvent<HTMLInputElement>)}
                />
              </Box>

              <Box component="div" display="flex" justifyContent="end" height="0.5" paddingTop="0.5rem">
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  sx={{ mr: 2 }}
                  className={props.classes?.button}
                  disabled={!props.patientActive}
                  onClick={() => {
                    refetchPrescriptions();
                  }}
                >
                  <i className="fa fa-refresh"></i>
                </Button>
                {handleCreateNewScriptBtn()}
              </Box>
            </Box>
            <PrescriptionsResults />
          </div>
        )}
      </SectionBody>
      <PrescribeErrorModal open={prescribeStateError} onConfirm={hidePrescribeStateError} />
    </>
  );
};

export default Prescriptions;
